import get from "lodash/get";
import find from "lodash/find";
import appendTypeAndCountry from "../../../utils/append-type-and-country";
import Profile from "./Profile";
import Dashboard from "./components/Dashboard";
import Questionnaire from "./components/Questionnaire";
import Tasks from "./components/Tasks";
import Guides from "./components/Guides";

import Accountability from "./components/Accountability";
import ConfirmRemoveAthlete from "./components/ConfirmRemoveAthlete";
import ConfirmStopTraining from "./components/ConfirmStopTraining";
import AccountabilityTempl from "../../../parts/modals/profile/DailyAccountability/AccountabilityTempl";
import AddAccountabilityTemplate from "../../../parts/modals/profile/DailyAccountability/AddAccountabilityTemplate";
import QuestionnaireTemplates from "../../../parts/modals/profile/QuestionnaireTemplates";
import QuestionnaireSchedule from "../../../parts/modals/profile/QuestionnaireSchedule";
import AddQuestionnaireTemplate from "../../../parts/modals/profile/AddQuestionnaireTemplate";
import { whoIsAthleteCoach } from "../../../utils/user-helpers";
import VideoCenter from "./components/VideoCenter";
import VideoCenterWatch from "./components/VideoCenterWatch";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import AddGoal from "../../../parts/modals/profile/AddGoal";
import moment from "moment";
import DailyGoalsModal from "../../../parts/modals/profile/DailyGoalsModal";

export const getRoot = async ({ res, req, locals, load, momentTZ, flash }) => {
  const { query } = req;
  const modal = get(query, "modal", {});
  locals.athlete = appendTypeAndCountry(locals.athlete, momentTZ);

  if (query.copied) {
    flash("success", "Email address copied to clipboard");
    return res.refreshWith({ copied: false });
  }

  const { organizationOwner } = locals ?? undefined;

  const { section, templateId, athlete, organizationCoach, sharedUrl } = query;

  const coachOrganizationInformation =
    locals.coachOrganizationInformation || undefined;

  // here we are getting the ID of the coach based on if the athlete is from the logged in coach or not
  const organizationCoachId = organizationCoach
    ? whoIsAthleteCoach(
        locals.activeUser.athletes,
        athlete,
        locals.activeUser._id,
        organizationCoach
      )
    : undefined;

  // if the organization coach equals the logged in coach, we are going to load the logged in coach information
  // if not, we are going to load the organization coach information
  const userToLoadInformation =
    !!organizationCoachId &&
    String(organizationCoachId) !== String(locals.activeUser._id)
      ? coachOrganizationInformation
      : locals.activeUser;

  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin" ||
      locals.organizationRoleName === "Nutritionist");

  let template = null;
  if (templateId) {
    const userToUseTemplates = organizationOwner || userToLoadInformation;

    const field = `${section}Templates`;

    template = userToUseTemplates[field].templates.find(
      (item) => item._id === templateId
    );
  }

  // 30 days before the current date
  const dailyStart = moment().subtract(30, "days").format("YYYY-MM-DD");

  const loadPromises = [
    load("coachVideos", {
      athlete,
      coach: locals.activeUser._id,
    }),
    load("clientGoals", {
      proxyAthlete: athlete,
    }),
    load("dailyStats", {
      proxyAthlete: athlete,
      start: dailyStart,
    }),
    load("workflows"),
    load("onboardingQuestionnaire", { proxyAthlete: athlete }),
  ];

  if (locals.activeUser.organizationId) {
    loadPromises.push(load("usersOrganization"));
  }

  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  // Check if the athlete is inactive and prohibit loading activeExercises if so
  const currentAthlete = find(
    locals.activeUser.athletes,
    (athl) => String(athl._id) === String(athlete)
  );
  const athleteInactive = currentAthlete?.status?.inactive;

  if (!athleteInactive) {
    // I swear if I hear about performance issues on the dashboard for this
    loadPromises.push(
      load("activeExercises", {
        start: moment().subtract(30, "days").startOf("week"),
        end: moment(),
        proxyAthlete: athlete,
        reqSource: "calendar",
      })
    );
  }

  await Promise.all(loadPromises);

  const sections = {
    dashboard: <Dashboard athlete={locals.athlete} locals={locals} />,
    questionnaire: (
      <Questionnaire
        athlete={locals.athlete}
        origin={locals.origin}
        userToLoadInformation={userToLoadInformation}
        organizationCoach={organizationCoach}
        organizationOwner={organizationOwner}
      />
    ),
    accountability: (
      <Accountability
        athlete={locals.athlete}
        locals={locals}
        userToLoadInformation={userToLoadInformation}
        organizationCoach={organizationCoach}
        organizationOwner={organizationOwner}
      />
    ),
    tasks: (
      <Tasks
        athlete={locals.athlete}
        activeUserToRequest={locals.activeUserToRequest}
        coachOrganizationInformation={coachOrganizationInformation}
      />
    ),
    guides: (
      <Guides
        athlete={locals.athlete}
        coachOrganizationInformation={coachOrganizationInformation}
        activeUser={locals.activeUser}
      />
    ),
    coachvideos: <VideoCenter athlete={locals.athlete} locals={locals} />,
  };
  locals.modal = {
    "stop-training": <ConfirmStopTraining />,
    "remove-athlete": <ConfirmRemoveAthlete />,
    "accountability-templates": (
      <AccountabilityTempl
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
      />
    ),
    "add-accountability-template": (
      <AddAccountabilityTemplate
        defaultTemplate={
          userToLoadInformation.accountabilityTemplates?.defaultTemplate
        }
        template={template || false}
        athleteId={locals.athlete._id}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
        templateId={templateId}
      />
    ),
    "questionnaire-templates": (
      <QuestionnaireTemplates
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
      />
    ),
    "questionnaire-schedule": (
      <QuestionnaireSchedule
        startingCalendarDay={userToLoadInformation.startingCalendarDay || 0}
        athlete={locals.athlete}
        userToLoadInformation={userToLoadInformation}
      />
    ),
    "add-questionnaire-template": (
      <AddQuestionnaireTemplate
        template={template || null}
        userToLoadInformation={userToLoadInformation}
        organizationOwner={organizationOwner}
      />
    ),
    "watch-loom-video": (
      <VideoCenterWatch sharedUrl={sharedUrl} locals={locals} />
    ),
    "activity-feed": <ActivityDrawer />,
    "add-client-goal": <AddGoal locals={locals} />,
    "daily-goal-modal": <DailyGoalsModal locals={locals} />,
  }[modal.type];

  res.body = (
    <Profile athlete={locals.athlete} organizationCoachId={organizationCoachId}>
      {sections[section] ? sections[section] : sections.dashboard}
    </Profile>
  );
};
